import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Button, EmptyState, Input, Pagination, Select, Table, confirmDelete, notifyError, notifySuccess, type TableColumn } from "../ui";
import { useQuotesList } from "../../features/quotes/queries";
import { useDeleteQuote, useGenerateQuotePdf } from "../../features/quotes/mutations";
import { QUOTE_STATUSES, type Quote, type QuoteStatus } from "../../features/quotes/types";
import { useDebounce } from "../../hooks/useDebounce";
import { QuoteStatusBadge } from "./QuoteStatusBadge";
import { QuoteDetailModal } from "./QuoteDetailModal";
import { formatMoney } from "./QuoteTotals";

interface QuotesListProps {
  /** Filtros fijos (p. ej. desde el CRM: una empresa; desde un viaje: trip_id). */
  fixed?: { crm_company_id?: string; trip_id?: string; route_id?: string };
  pageSize?: number;
  /** Acción de crear la renderiza el padre; aquí sólo se pinta el vacío. */
  onCreate?: () => void;
}

function fmtDate(v: string | null | undefined): string {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-MX");
}

function place(p: { address?: string; lat?: number; lng?: number } | null | undefined): string {
  return p?.address || (p?.lat != null && p?.lng != null ? `${p.lat.toFixed(2)}, ${p.lng.toFixed(2)}` : "—");
}

/** Tabla de cotizaciones con filtros, paginación y detalle. Lee `?id=`, `?status=` y `?crm_company_id=` de la URL. */
export function QuotesList({ fixed, pageSize = 20, onCreate }: QuotesListProps) {
  const { t } = useTranslation("finanzasOperativas");
  const [params, setParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<QuoteStatus | "">((params.get("status") as QuoteStatus) || "");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 300);
  const openId = params.get("id");

  const query = useQuotesList({
    page,
    limit: pageSize,
    status,
    search: debouncedSearch || undefined,
    crm_company_id: fixed?.crm_company_id ?? params.get("crm_company_id") ?? undefined,
    trip_id: fixed?.trip_id,
    route_id: fixed?.route_id,
  });
  const delMut = useDeleteQuote();
  const pdfMut = useGenerateQuotePdf();

  const rows = query.data?.data ?? [];
  const totalPages = query.data?.meta?.totalPages ?? 1;

  const open = (id: string | null) => {
    const next = new URLSearchParams(params);
    if (id) next.set("id", id);
    else next.delete("id");
    setParams(next, { replace: true });
  };

  const remove = async (q: Quote) => {
    const ok = await confirmDelete({ title: t("quotes.confirmDelete.title"), text: t("quotes.confirmDelete.text", { folio: q.folio }) });
    if (!ok) return;
    try {
      await delMut.mutateAsync(q.id);
      void notifySuccess(t("quotes.toasts.deleted"));
    } catch (err) {
      void notifyError(t("quotes.toasts.error"), (err as Error)?.message);
    }
  };

  const pdf = async (q: Quote) => {
    if (q.pdf?.url && !q.pdf.stale) {
      window.open(q.pdf.url, "_blank", "noopener");
      return;
    }
    try {
      const meta = await pdfMut.mutateAsync({ id: q.id, show_breakdown: q.show_breakdown });
      if (meta?.url) window.open(meta.url, "_blank", "noopener");
    } catch (err) {
      void notifyError(t("quotes.toasts.error"), (err as Error)?.message);
    }
  };

  const columns: TableColumn<Quote>[] = useMemo(
    () => [
      { key: "folio", header: t("quotes.list.columns.folio"), render: (q) => <span className="font-medium text-content">{q.folio}</span> },
      { key: "customer", header: t("quotes.list.columns.customer"), render: (q) => q.customer_snapshot?.name || "—" },
      { key: "route", header: t("quotes.list.columns.route"), hideOnMobile: true, render: (q) => `${place(q.origin)} → ${place(q.destination)}` },
      { key: "vehicleClass", header: t("quotes.list.columns.vehicleClass"), hideOnMobile: true, render: (q) => t(`costProfiles.vehicleClasses.${q.vehicle_class}`, { defaultValue: q.vehicle_class }) },
      { key: "cost", header: t("quotes.list.columns.cost"), align: "right", hideOnMobile: true, render: (q) => formatMoney(q.cost_total, q.currency) },
      { key: "price", header: t("quotes.list.columns.price"), align: "right", render: (q) => <span className="font-semibold">{formatMoney(q.price, q.currency)}</span> },
      { key: "margin", header: t("quotes.list.columns.margin"), align: "right", hideOnMobile: true, render: (q) => `${Number(q.margin_pct).toFixed(1)} %` },
      { key: "status", header: t("quotes.list.columns.status"), render: (q) => <QuoteStatusBadge status={q.status} size="sm" /> },
      { key: "validUntil", header: t("quotes.list.columns.validUntil"), hideOnMobile: true, render: (q) => fmtDate(q.valid_until) },
      {
        key: "actions",
        header: t("quotes.list.columns.actions"),
        align: "right",
        render: (q) => (
          <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Button variant="ghost" size="sm" iconOnly title={t("quotes.actions.view")} icon={<i className="ri-eye-line" />} onClick={() => open(q.id)} />
            <Button variant="ghost" size="sm" iconOnly title={t("quotes.actions.pdf")} icon={<i className="ri-file-pdf-2-line" />} onClick={() => void pdf(q)} />
            <Button variant="dangerSoft" size="sm" iconOnly title={t("quotes.actions.delete")} disabled={q.status === "accepted"} icon={<i className="ri-delete-bin-line" />} onClick={() => void remove(q)} />
          </div>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3">
        <Input
          containerClassName="min-w-56 flex-1"
          className="h-10 bg-app"
          placeholder={t("quotes.list.searchPlaceholder")}
          icon={<i className="ri-search-line" />}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(1);
          }}
        />
        <Select
          containerClassName="w-48"
          className="h-10 bg-app"
          value={status}
          onChange={(e) => {
            setStatus(e.target.value as QuoteStatus | "");
            setPage(1);
          }}
          options={[{ value: "", label: t("quotes.list.allStatuses") }, ...QUOTE_STATUSES.map((s) => ({ value: s, label: t(`quotes.statuses.${s}`) }))]}
        />
      </div>

      {query.isError ? (
        <EmptyState icon="ri-error-warning-line" title={t("sections.cotizaciones.error.title")} description={t("sections.cotizaciones.error.description")} action={<Button size="sm" onClick={() => void query.refetch()}>{t("common.retry")}</Button>} />
      ) : !query.isLoading && rows.length === 0 ? (
        <EmptyState
          icon="ri-file-list-3-line"
          title={t("sections.cotizaciones.empty.title")}
          description={t("sections.cotizaciones.empty.description")}
          action={onCreate ? <Button size="sm" icon={<i className="ri-add-line" />} onClick={onCreate}>{t("sections.cotizaciones.new")}</Button> : undefined}
        />
      ) : (
        <>
          <Table<Quote> columns={columns} rows={rows} rowKey={(q) => q.id} loading={query.isLoading} onRowClick={(q) => open(q.id)} compact />
          <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} hideWhenSingle />
        </>
      )}

      {openId && <QuoteDetailModal quoteId={openId} initial={rows.find((q) => q.id === openId) ?? null} onClose={() => open(null)} />}
    </div>
  );
}
